import React, { useState, useMemo, useRef, useEffect } from "react";
import { LiveNumber } from "@/components/LiveNumber";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList
} from "recharts";
import { calculCotisationsTotal_micro, calculBeneficeMicro, calculIR, SEUIL_MICRO_BNC, SEUIL_TVA_FRANCHISE } from "@/utils/fiscal";
import { useSubscription } from "@/hooks/useSubscription";
import { useSimulationLimit } from "@/hooks/useSimulationLimit";
import PaywallCard from "@/components/PaywallCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", blue: "#2E6DA4",
  light: "#EAF2FB", lightTeal: "#E8F5F4", danger: "#E63946",
  warning: "#F4A261", muted: "#6B85A3", border: "#D6E4F0",
  bg: "#F4F8FC", white: "#FFFFFF", purple: "#7C5CFC",
  indigo: "#4F46E5", indigoBg: "#EEF2FF",
};

const TAUX_URSSAF_BNC = 0.40; // ~40% estimation BNC classique (taux moyen constaté 35-45% du bénéfice net)
const SEUIL_CA = SEUIL_MICRO_BNC;
const SEUIL_TVA = SEUIL_TVA_FRANCHISE;
const SEUIL_TVA_MAJORE = 41250;

const fmt = (n: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const ZERO_RESULT = { urssaf: 0, ir: 0, charges: 0, net: 0, imposable: 0 };

function calcNet(ca: number, isMicro: boolean, chargesReelles: number): typeof ZERO_RESULT | null {
  if (isMicro) {
    const urssaf = calculCotisationsTotal_micro(ca);
    const imposable = calculBeneficeMicro(ca);
    const ir = calculIR(imposable);
    return { urssaf, ir, charges: 0, net: ca - urssaf - ir, imposable };
  } else {
    if (chargesReelles <= 0) return null;
    const charges = chargesReelles;
    const benefice = Math.max(0, ca - charges);
    const urssaf = benefice * TAUX_URSSAF_BNC; // ~40% estimation BNC classique
    const beneficeImposable = Math.max(0, benefice - urssaf);
    const ir = calculIR(beneficeImposable);
    return { urssaf, ir, charges, net: ca - charges - urssaf - ir, imposable: beneficeImposable };
  }
}

const Card = ({ children, className = "", style = {} }: { children?: React.ReactNode; className?: string; style?: React.CSSProperties }) => (
  <div className={`rounded-xl bg-white shadow-sm border p-5 ${className}`} style={{ borderColor: C.border, borderRadius: 12, ...style }}>
    {children}
  </div>
);

interface ProjectionProps {
  caAnnuel: number;
  revenuNetActuel: number;
  moyenneMensuelle: number;
  onRetour: () => void;
  isMicroBNC?: boolean;
  seancesAnnuelles?: number;
  objectifCA?: number;
  chargesReelles?: number;
  tarifActuel?: number;
  onNavigateCharges?: () => void;
  semainesTravaillees?: number;
}

export default function Projection({
  caAnnuel, revenuNetActuel, moyenneMensuelle, onRetour,
  isMicroBNC = true, seancesAnnuelles = 0, objectifCA = 0,
  chargesReelles = 0, tarifActuel = 60, onNavigateCharges,
  semainesTravaillees = 45,
}: ProjectionProps) {
  const { plan, isTrial, trialUsed } = useSubscription();
  const proCta = trialUsed ? "Passer au Pro →" : "Passer au Pro — 14 jours gratuits →";
  const isPro = plan === "pro";
  const isEssentiel = !isPro;
  const { count, limitReached, remaining, increment } = useSimulationLimit("projection");
  const [showLimitModal, setShowLimitModal] = useState(false);
  const navigateTo = useNavigate();

  const hasCountedRef = useRef(false);
  const trackUsage = () => {
    if (isEssentiel && !hasCountedRef.current) {
      hasCountedRef.current = true;
      increment();
    }
  };

  useEffect(() => {
    if (isEssentiel && limitReached) {
      setShowLimitModal(true);
    }
  }, [isEssentiel, limitReached]);
  // Derive current patients/week from data
  const estimatedPatientsWeek = useMemo(() => {
    if (seancesAnnuelles > 0 && caAnnuel > 0) {
      return Math.round(seancesAnnuelles / semainesTravaillees);
    }
    if (caAnnuel > 0 && tarifActuel > 0) {
      return Math.round(caAnnuel / tarifActuel / semainesTravaillees);
    }
    return 10;
  }, [seancesAnnuelles, caAnnuel, tarifActuel, semainesTravaillees]);

  const topRef = useRef<HTMLDivElement>(null);
  const [patients, setPatients] = useState(Math.max(1, estimatedPatientsWeek));
  const [tarif, setTarif] = useState(tarifActuel || 60);
  const [semaines, setSemaines] = useState(semainesTravaillees);
  const [dureeSeance, setDureeSeance] = useState(60);
  const [conges, setConges] = useState(8);
  const [objectifNet, setObjectifNet] = useState(
    objectifCA > 0
      ? (isMicroBNC ? Math.round(objectifCA * 0.60 / 12) : Math.round(objectifCA * 0.45 / 12))
      : 3000
  );

  // ─── CALCULS PRINCIPAUX ─────────────────────────────────────────
  const calc = useMemo(() => {
    const ca = patients * tarif * semaines;
    const result = calcNet(ca, isMicroBNC, chargesReelles) ?? ZERO_RESULT;
    const netMois = result.net / 12;
    const heuresAn = (patients * semaines * dureeSeance) / 60;
    const tauxHoraire = heuresAn > 0 ? result.net / heuresAn : 0;

    // Situation actuelle pour comparaison
    const currentResult = calcNet(caAnnuel, isMicroBNC, chargesReelles) ?? ZERO_RESULT;

    // Jours de liberté équivalent
    const diffNet = result.net - currentResult.net;
    const insufficientData = caAnnuel < 1000;
    let joursCongeEquiv = 0;
    if (!insufficientData && currentResult.net > 0) {
      const gainJournalier = currentResult.net / 365;
      joursCongeEquiv = Math.round(diffNet / gainJournalier);
      joursCongeEquiv = Math.max(-365, Math.min(365, joursCongeEquiv));
    }

    // ─── TRÉSORERIE DE SÉCURITÉ (Hormozi Buffer) ───
    // Cotisations provisionnelles basées sur le CA actuel
    const cotisationsProvisionnelles = isMicroBNC
      ? calculCotisationsTotal_micro(caAnnuel)
      : Math.round(Math.max(0, caAnnuel - chargesReelles) * TAUX_URSSAF_BNC);
    // Cotisations réelles dues sur le CA projeté
    const cotisationsReellesDues = isMicroBNC
      ? calculCotisationsTotal_micro(ca)
      : Math.round(Math.max(0, ca - chargesReelles) * TAUX_URSSAF_BNC);
    // Régularisation N+1
    const regularisationN1 = Math.max(0, cotisationsReellesDues - cotisationsProvisionnelles);
    // IR provisionnel vs projeté
    const irProvisionnel = calculIR(isMicroBNC ? calculBeneficeMicro(caAnnuel) : Math.max(0, Math.max(0, caAnnuel - chargesReelles) - cotisationsProvisionnelles));
    const irProjecte = calculIR(isMicroBNC ? calculBeneficeMicro(ca) : Math.max(0, Math.max(0, ca - chargesReelles) - cotisationsReellesDues));
    const regularisationIR = Math.max(0, irProjecte - irProvisionnel);
    // Total régularisation + 2% marge de sécurité
    const totalRegularisation = regularisationN1 + regularisationIR;
    const margeSecurite = Math.round(totalRegularisation * 0.02);
    const tresorerieSecurite = totalRegularisation + margeSecurite;
    const tresorerieMensuelle = Math.round(tresorerieSecurite / 12);

    // Charges fixes mensuelles (ne disparaissent pas si CA = 0 sur un mois)
    const chargesFixesMensuelles = Math.round(chargesReelles / 12);

    return {
      ca, ...result, netMois, tauxHoraire, currentResult, diffNet, joursCongeEquiv, heuresAn, insufficientData,
      cotisationsProvisionnelles, cotisationsReellesDues, regularisationN1, regularisationIR,
      totalRegularisation, margeSecurite, tresorerieSecurite, tresorerieMensuelle, chargesFixesMensuelles,
    };
  }, [patients, tarif, semaines, dureeSeance, isMicroBNC, chargesReelles, caAnnuel]);

  // ─── SCÉNARIOS GRAPHIQUE ────────────────────────────────────────
  const scenarios = useMemo(() => {
    const current = calcNet(caAnnuel, isMicroBNC, chargesReelles) ?? ZERO_RESULT;
    const slider = calcNet(patients * tarif * semaines, isMicroBNC, chargesReelles) ?? ZERO_RESULT;
    const plus2 = calcNet((patients + 2) * tarif * semaines, isMicroBNC, chargesReelles) ?? ZERO_RESULT;
    const plus5 = calcNet((patients + 5) * tarif * semaines, isMicroBNC, chargesReelles) ?? ZERO_RESULT;
    const tarifPlus10 = calcNet(patients * (tarif + 10) * semaines, isMicroBNC, chargesReelles) ?? ZERO_RESULT;
    const isLowCA = caAnnuel < 1000;
    return [
      { name: isLowCA ? "Données en cours (année débutante)" : "Actuel", net: isLowCA ? 0 : Math.round(current.net), urssaf: Math.round(current.urssaf), ir: Math.round(current.ir), charges: Math.round(current.charges), fill: C.muted },
      { name: "Votre scénario", net: Math.round(slider.net), urssaf: Math.round(slider.urssaf), ir: Math.round(slider.ir), charges: Math.round(slider.charges), fill: C.teal },
      { name: "+2 patients", net: Math.round(plus2.net), urssaf: Math.round(plus2.urssaf), ir: Math.round(plus2.ir), charges: Math.round(plus2.charges), fill: "#5BBFB5" },
      { name: "+5 patients", net: Math.round(plus5.net), urssaf: Math.round(plus5.urssaf), ir: Math.round(plus5.ir), charges: Math.round(plus5.charges), fill: "#3A8A80" },
      { name: `Tarif +10€`, net: Math.round(tarifPlus10.net), urssaf: Math.round(tarifPlus10.urssaf), ir: Math.round(tarifPlus10.ir), charges: Math.round(tarifPlus10.charges), fill: C.navy },
    ];
  }, [caAnnuel, patients, tarif, semaines, isMicroBNC, chargesReelles]);

  // ─── CONGÉS ──────────────────────────────────────────────────────
  const congesCalc = useMemo(() => {
    const semainesTravaillees = 52 - conges;
    const ca = patients * tarif * semainesTravaillees;
    const result = calcNet(ca, isMicroBNC, chargesReelles) ?? ZERO_RESULT;
    const baseResult = calcNet(patients * tarif * semaines, isMicroBNC, chargesReelles) ?? ZERO_RESULT;
    const diff = result.net - baseResult.net;
    return { ...result, ca, semainesTravaillees, diff };
  }, [patients, tarif, conges, isMicroBNC, chargesReelles]);

  // ─── OBJECTIF INVERSÉ ───────────────────────────────────────────
  const objectifCalc = useMemo(() => {
    const targetAnnuel = objectifNet * 12;
    // Binary search for required patients
    let lo = 1, hi = 60, best = 1;
    for (let i = 0; i < 30; i++) {
      const mid = Math.round((lo + hi) / 2);
      const ca = mid * tarif * semaines;
      const r = calcNet(ca, isMicroBNC, chargesReelles) ?? ZERO_RESULT;
      if (r.net >= targetAnnuel) { best = mid; hi = mid; }
      else { lo = mid + 1; }
    }
    const ca = best * tarif * semaines;
    const r = calcNet(ca, isMicroBNC, chargesReelles) ?? ZERO_RESULT;
    return { patients: best, ca, ...r };
  }, [objectifNet, tarif, semaines, isMicroBNC, chargesReelles]);

  // ─── ALERTES ────────────────────────────────────────────────────
  const alertes = useMemo(() => {
    const alerts: { bg: string; border: string; color: string; icon: string; text: string }[] = [];
    if (isMicroBNC) {
      if (calc.ca > SEUIL_CA) {
        const bncResult = calcNet(calc.ca, false, calc.ca * 0.35);
        alerts.push({
          bg: "#FFF0F1", border: C.danger, color: C.danger, icon: "⚠️",
          text: `Attention — vous dépasseriez le seuil Micro-BNC (${fmt(SEUIL_CA)}). Vous basculerez en BNC classique. Votre revenu net serait de ${fmt(bncResult?.net ?? 0)} en BNC classique vs ${fmt(calc.net)} en Micro-BNC avec ce CA.`
        });
      } else if (calc.ca >= SEUIL_CA * 0.85) {
        alerts.push({
          bg: "#FFF8F0", border: C.warning, color: "#C46200", icon: "🟠",
          text: `Attention, vous approchez du seuil Micro-BNC (${fmt(SEUIL_CA)}). Une bascule au Réel pourrait changer votre rentabilité. Il vous reste ${fmt(SEUIL_CA - calc.ca)} de marge.`
        });
      }
    }
    // TVA — soins psychologiques exonérés (Art. 261, 4-1° CGI)
    alerts.push({
      bg: C.lightTeal, border: C.teal, color: C.teal, icon: "✅",
      text: `Vos actes de soins psychologiques sont exonérés de TVA sans limite de seuil (Art. 261, 4-1° du CGI). Le prévisionnel est calculé sur du CA net de TVA.`
    });
    return alerts;
  }, [calc.ca, calc.net, isMicroBNC]);

  const seancesMois = patients * (semaines / 12);

  // ─── SLIDER COMPONENT ──────────────────────────────────────────
  const Slider = ({ icon, label, value, min, max, step, onChange, format }: {
    icon: string; label: string; value: number; min: number; max: number; step: number;
    onChange: (v: number) => void; format: (v: number) => string;
  }) => (
    <div className="flex-1 min-w-[200px]">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.muted }}>{label}</span>
      </div>
      <div className="text-center mb-2">
        <span className="inline-block px-3 py-1 rounded-full text-sm font-bold text-white" style={{ backgroundColor: C.teal }}>
          {format(value)}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-2.5 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, ${C.teal} ${((value - min) / (max - min)) * 100}%, ${C.border} ${((value - min) / (max - min)) * 100}%)`,
        }}
      />
      <div className="flex justify-between text-[10px] mt-1" style={{ color: C.muted }}>
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );

  const ChartTooltipCustom = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="rounded-xl shadow-lg p-3 text-sm" style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}>
        <div className="font-semibold mb-2" style={{ color: C.navy }}>{label}</div>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between gap-4"><span style={{ color: C.muted }}>URSSAF</span><span className="font-bold" style={{ color: C.blue }}>- {fmt(d?.urssaf || 0)}</span></div>
          <div className="flex justify-between gap-4"><span style={{ color: C.muted }}>Provision IR</span><span className="font-bold" style={{ color: C.warning }}>- {fmt(d?.ir || 0)}</span></div>
          {!isMicroBNC && <div className="flex justify-between gap-4"><span style={{ color: C.muted }}>Charges</span><span className="font-bold" style={{ color: C.muted }}>- {fmt(d?.charges || 0)}</span></div>}
          <hr style={{ borderColor: C.border }} />
          <div className="flex justify-between gap-4"><span style={{ color: C.teal }} className="font-semibold">Ce que vous touchez vraiment</span><span className="font-bold" style={{ color: C.teal }}>{fmt(d?.net || 0)}</span></div>
        </div>
      </div>
    );
  };

  const InfoTooltip = ({ text }: { text: string }) => {
    const [show, setShow] = useState(false);
    return (
      <span className="relative inline-block ml-1 align-middle">
        <span
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          className="cursor-help text-xs select-none"
          style={{ color: C.muted }}
        >
          ⓘ
        </span>
        {show && (
          <span
            className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 rounded-xl px-3 py-2 text-xs leading-relaxed shadow-xl pointer-events-none"
            style={{ backgroundColor: C.navy, color: C.white, width: 240, display: "block" }}
          >
            {text}
          </span>
        )}
      </span>
    );
  };

  if (caAnnuel === 0) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <div className="text-center py-10">
            <span className="text-5xl mb-4 block">🚀</span>
            <div className="font-bold text-lg mb-2" style={{ color: C.navy }}>Projection personnalisée</div>
            <div className="text-sm" style={{ color: C.muted }}>
              Commencez par saisir vos recettes dans l'onglet Saisir pour obtenir une projection personnalisée basée sur votre activité réelle.
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (isEssentiel && limitReached) {
    return (
      <>
        <Dialog open={showLimitModal} onOpenChange={setShowLimitModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ color: "#1B3A5C" }}>Vous avez utilisé vos 3 simulations gratuites ce mois-ci</DialogTitle>
              <DialogDescription>Simulez sans limite avec le plan Pro.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 mt-4">
              <button
                onClick={() => navigateTo("/pricing")}
                className="w-full py-3 rounded-xl text-white font-bold text-sm"
                style={{ backgroundColor: "#2A9D8F" }}
              >
                {proCta}
              </button>
              <button
                onClick={() => { setShowLimitModal(false); onRetour(); }}
                className="w-full py-3 rounded-xl text-sm font-semibold border"
                style={{ borderColor: "#D6E4F0", color: "#1B3A5C" }}
              >
                Revenir au dashboard
              </button>
            </div>
          </DialogContent>
        </Dialog>
        <PaywallCard
          icon="📈"
          title="Vous avez utilisé vos 3 projections gratuites ce mois-ci"
          description="Projetez sans limite avec le plan Pro."
          ctaLabel={proCta}
        />
      </>
    );
  }

  return (
    <div style={{ backgroundColor: C.bg }} className="min-h-0">
      <div className="max-w-[820px] mx-auto px-4 py-6 space-y-6">

        {/* ═══ HEADER ═══ */}
        <div ref={topRef} className="text-center mb-2">
          <h1 className="text-xl md:text-2xl font-bold" style={{ color: C.navy }}>
            Et si je faisais évoluer mon activité ? 🚀
          </h1>
          <p className="text-sm mt-1" style={{ color: C.muted }}>
            Simulez l'impact réel sur votre revenu net — après URSSAF, charges et impôts
          </p>
        </div>

        {/* ═══ SECTION 1 — HYPOTHÈSES ═══ */}
        <Card>
          <div className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: C.muted }}>
            1 · Vos hypothèses de rythme
          </div>
          <div className="flex flex-col md:flex-row gap-6">
            <Slider icon="🧠" label="Patients par semaine" value={patients} min={1} max={30} step={1}
              onChange={v => { trackUsage(); setPatients(v); }} format={v => `${v} patient${v > 1 ? "s" : ""}`} />
            <Slider icon="💶" label="Tarif par séance" value={tarif} min={30} max={150} step={5}
              onChange={v => { trackUsage(); setTarif(v); }} format={v => `${v} €`} />
            <Slider icon="📅" label="Semaines travaillées/an" value={semaines} min={30} max={52} step={1}
              onChange={v => { trackUsage(); setSemaines(v); }} format={v => `${v} sem.`} />
          </div>
          {/* ── La Formule Magique ── */}
          <div className="mt-5 rounded-xl p-4" style={{ backgroundColor: C.lightTeal, border: `1px solid ${C.teal}` }}>
            <div className="text-[10px] font-bold uppercase tracking-widest mb-2 text-center" style={{ color: C.teal }}>
              Formule de calcul
            </div>
            <div className="text-center font-bold" style={{ color: C.navy, fontSize: 15 }}>
              <span style={{ color: C.teal }}>{patients} séances/sem</span>
              {" × "}
              <span style={{ color: C.teal }}>{tarif} €</span>
              {" × "}
              <span style={{ color: C.teal }}>{semaines} semaines</span>
              {" = "}
              <span style={{ fontSize: 17 }}><LiveNumber value={calc.ca} fmt={fmt} baseColor={C.navy} /></span>
            </div>
            <div className="text-xs text-center mt-1.5" style={{ color: C.muted }}>
              ≈ {Math.round(seancesMois)} séances/mois · {fmt(calc.ca / 12)}/mois brut
            </div>
          </div>
        </Card>

        {/* ═══ SECTION 2 — RÉSULTATS ═══ */}
        {!isMicroBNC && chargesReelles <= 0 ? (
          <div className="rounded-xl p-4 border-l-4" style={{ backgroundColor: "#FFF8F0", borderLeftColor: C.warning, color: "#C46200" }}>
            <div className="font-semibold mb-2">📋 Saisissez vos charges dans l'onglet Charges 2035 pour une projection BNC fiable</div>
            <p className="text-sm mb-3" style={{ color: "#A05500" }}>
              En BNC classique, votre revenu net dépend de vos charges déductibles réelles. Sans ces données, la projection ne peut pas être calculée précisément.
            </p>
            <button
              onClick={onNavigateCharges}
              className="inline-block text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors hover:opacity-80"
              style={{ borderColor: C.warning, color: "#C46200" }}
            >
              → Saisir mes charges
            </button>
          </div>
        ) : (
        <>
        <div className="text-xs font-bold uppercase tracking-widest mb-1 mt-2" style={{ color: C.muted }}>
          2 · Résultats
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Card 1 — Vrai Net mensuel */}
          <Card style={{ borderColor: C.indigo, borderWidth: 2 }}>
            <div className="text-xs font-semibold uppercase tracking-wide mb-1 flex items-center gap-1" style={{ color: C.indigo }}>
              Vrai Net — votre pouvoir d'achat réel
            </div>
            <div className="text-3xl font-bold" style={{ fontSize: 32 }}><LiveNumber value={calc.netMois} fmt={fmt} baseColor={C.indigo} /><span className="text-base font-semibold ml-1" style={{ color: C.indigo }}>/mois</span></div>
            <div className="mt-2 space-y-0.5 text-xs" style={{ color: C.muted }}>
              <div style={{ color: C.warning }}>− Cotisations {isMicroBNC ? "CIPAV 23,4%" : "~40%"} : {fmt(calc.urssaf / 12)}/mois</div>
              <div style={{ color: C.warning }}>− Provision impôt : {fmt(calc.ir / 12)}/mois</div>
              {!isMicroBNC && <div style={{ color: C.warning }}>− Charges : {fmt(calc.charges / 12)}/mois</div>}
            </div>
          </Card>

          {/* Card 2 — Vrai Net annuel */}
          <Card>
            <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: C.muted }}>
              Vrai Net annuel (votre pouvoir d'achat)
            </div>
            <div className="text-3xl font-bold" style={{ fontSize: 32 }}><LiveNumber value={Math.max(0, calc.net)} fmt={fmt} baseColor={C.indigo} /></div>
            <div className="mt-2 text-xs" style={{ color: C.muted }}>
              = {fmt(Math.max(0, calc.netMois))}/mois × 12
            </div>
          </Card>

          {/* Card 3 — Taux horaire */}
          <Card>
            <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: C.muted }}>
              Ce que vous gagnez vraiment par heure de travail
            </div>
            <div className="text-2xl font-bold" style={{ fontSize: 28 }}><LiveNumber value={calc.tauxHoraire} fmt={fmt} baseColor={C.teal} />/h</div>
            <div className="flex gap-1 mt-3">
              {[45, 50, 60].map(d => (
                <button key={d} onClick={() => setDureeSeance(d)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  style={{
                    backgroundColor: dureeSeance === d ? C.teal : C.light,
                    color: dureeSeance === d ? C.white : C.navy,
                  }}>
                  {d} min
                </button>
              ))}
            </div>
          </Card>

          {/* Card 4 — Jours de congé */}
          <Card>
            <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: C.muted }}>
              🏖️ L'équivalent en jours de liberté
            </div>
            {calc.insufficientData ? (
              <div className="text-sm mt-2 italic" style={{ color: C.muted }}>
                Saisissez au moins 1 000€ de recettes dans l'onglet Saisir pour activer cette fonctionnalité
              </div>
            ) : calc.joursCongeEquiv > 0 ? (
              <>
                <div className="text-2xl font-bold" style={{ color: C.teal, fontSize: 28 }}>
                  +{calc.joursCongeEquiv} jours
                </div>
                <div className="text-xs mt-1" style={{ color: C.muted }}>
                  de congés payés équivalents vs votre situation actuelle
                </div>
              </>
            ) : calc.joursCongeEquiv === 0 ? (
              <div className="text-sm mt-2" style={{ color: C.muted }}>
                Scénario identique à votre situation actuelle
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold" style={{ color: C.warning, fontSize: 28 }}>
                  {calc.joursCongeEquiv} jours
                </div>
                <div className="text-sm mt-1 font-semibold" style={{ color: C.warning }}>
                  ⚠️ Scénario moins favorable que votre situation actuelle
                </div>
              </>
            )}
          </Card>
        </div>
        </>
        )}

        {/* ═══ SECTION 3 — DÉTAIL DES PRÉLÈVEMENTS ═══ */}
        {(isMicroBNC || chargesReelles > 0) && (
        <Card>
          <div className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: C.muted }}>
            3 · Détail des prélèvements
          </div>
          <div className="space-y-1 text-sm">
            {/* CA */}
            <div className="flex items-center justify-between py-2">
              <span style={{ color: C.muted }}>CA annuel brut</span>
              <span className="font-bold" style={{ color: C.teal }}>{fmt(calc.ca)}</span>
            </div>
            {/* Cotisations */}
            <div className="flex items-center justify-between py-2 border-t" style={{ borderColor: C.border }}>
              <span className="flex items-center gap-1" style={{ color: C.muted }}>
                — Cotisations {isMicroBNC ? "CIPAV (23,4% du CA)" : "URSSAF (~40% du bénéfice)"}
                <InfoTooltip text={isMicroBNC
                  ? "En Micro-BNC : cotisations CIPAV = 23,2% du CA + 0,2% CFP = 23,4% total. L'abattement forfaitaire de 34% s'applique ensuite pour calculer la base imposable à l'IR."
                  : "En BNC classique : cotisations calculées sur le bénéfice net (CA − charges réelles). Taux moyen constaté 35–45%, estimé ici à 40%."} />
              </span>
              <span className="font-bold" style={{ color: C.warning }}>− {fmt(calc.urssaf)}</span>
            </div>
            {/* Charges BNC */}
            {!isMicroBNC && calc.charges > 0 && (
              <div className="flex items-center justify-between py-2 border-t" style={{ borderColor: C.border }}>
                <span style={{ color: C.muted }}>— Charges professionnelles déductibles</span>
                <span className="font-bold" style={{ color: C.warning }}>− {fmt(calc.charges)}</span>
              </div>
            )}
            {/* Intermédiaire : net de cotisations */}
            <div className="flex items-center justify-between px-3 py-2.5 rounded-xl border-t" style={{ borderColor: C.border, backgroundColor: C.bg }}>
              <span className="font-semibold text-sm" style={{ color: C.navy }}>
                Ce qu'il vous reste{" "}
                <span className="font-normal text-xs" style={{ color: C.muted }}>(net de cotisations)</span>
              </span>
              <span className="font-bold" style={{ color: C.navy }}>{fmt(Math.max(0, calc.ca - calc.urssaf - calc.charges))}</span>
            </div>
            {/* IR */}
            <div className="flex items-center justify-between py-2 border-t" style={{ borderColor: C.border }}>
              <span className="flex items-center gap-1" style={{ color: C.muted }}>
                — Provision pour impôt sur le revenu
                <InfoTooltip text={isMicroBNC
                  ? `Barème progressif IR 2026 calculé sur le bénéfice imposable après abattement forfaitaire de 34% (base : ${fmt(calc.imposable)}).`
                  : `Barème progressif IR 2026 calculé sur le bénéfice net de cotisations (${fmt(calc.imposable)}).`} />
              </span>
              <span className="font-bold" style={{ color: C.warning }}>− {fmt(calc.ir)}</span>
            </div>
            {/* Résultat final */}
            <div className="flex items-center justify-between px-4 py-3 rounded-xl mt-2" style={{ backgroundColor: C.indigoBg, border: `2px solid ${C.indigo}` }}>
              <div>
                <div className="font-bold text-sm" style={{ color: C.indigo }}>Vrai Net — votre pouvoir d'achat réel</div>
                <div className="text-xs mt-0.5" style={{ color: C.muted }}>{fmt(Math.max(0, calc.netMois))}/mois</div>
              </div>
              <div className="font-bold text-xl" style={{ color: C.indigo }}>{fmt(Math.max(0, calc.net))}</div>
            </div>
          </div>
        </Card>
        )}

        {/* ═══ SECTION 4 — GRAPHIQUE COMPARATIF ═══ */}
        <Card>
          <div className="text-sm font-bold mb-4" style={{ color: C.navy }}>
            📊 Vrai Net selon différents scénarios
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={scenarios} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false}
                tickFormatter={(v: number) => fmt(v)} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: C.navy }} axisLine={false} tickLine={false} width={110} />
              <Tooltip content={<ChartTooltipCustom />} />
              <Bar dataKey="net" name="Vrai Net — votre pouvoir d'achat réel" radius={[0, 6, 6, 0]} maxBarSize={36}>
                {scenarios.map((s, i) => <Cell key={i} fill={s.fill} />)}
                <LabelList dataKey="net" position="right" formatter={(v: number) => fmt(v)}
                  style={{ fontSize: 11, fontWeight: 600, fill: C.navy }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ═══ SECTION 4 — SEUILS ET ALERTES ═══ */}
        {alertes.length > 0 && (
          <div className="space-y-3">
            {alertes.map((a, i) => (
              <div key={i} className="rounded-xl px-5 py-3 text-sm border-l-4"
                style={{ backgroundColor: a.bg, borderLeftColor: a.border, color: a.color }}>
                {a.icon} {a.text}
              </div>
            ))}
          </div>
        )}

        {/* ═══ SECTION 5 — CONGÉS ═══ */}
        <Card>
          <div className="font-bold text-sm mb-4" style={{ color: C.navy }}>
            🏖️ Et si je prenais plus de congés ?
          </div>
          <Slider icon="🌴" label="Semaines de congés/an" value={conges} min={4} max={20} step={1}
            onChange={setConges} format={v => `${v} semaines`} />
          <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: C.light }}>
            <div className="text-sm" style={{ color: C.navy }}>
              En prenant <strong>{conges} semaines de congés</strong>, votre revenu net annuel serait de{" "}
              <strong style={{ color: C.teal }}>{fmt(congesCalc.net)}</strong>{" "}
              soit <strong style={{ color: C.teal }}>{fmt(congesCalc.net / 12)}/mois</strong>
              {congesCalc.diff !== 0 && (
                <span> — soit <strong style={{ color: congesCalc.diff < 0 ? C.warning : C.teal }}>
                  {congesCalc.diff >= 0 ? "+" : ""}{fmt(congesCalc.diff)}
                </strong> {congesCalc.diff < 0 ? "de moins" : "de plus"} par rapport à votre scénario principal ({semaines} semaines travaillées).</span>
              )}
            </div>
          </div>
          <div className="mt-3 text-sm italic" style={{ color: C.teal }}>
            💚 Le burn-out coûte plus cher que les congés — votre santé mentale a une valeur financière aussi
          </div>
        </Card>

        {/* ═══ TRÉSORERIE DE SÉCURITÉ (Hormozi Buffer) ═══ */}
        {calc.tresorerieSecurite > 0 && calc.ca > caAnnuel && (
          <Card style={{ borderColor: C.purple, borderWidth: 2 }}>
            <div className="font-bold text-sm mb-3 flex items-center gap-2" style={{ color: C.navy }}>
              <span className="text-xl">🛡️</span> Trésorerie de Sécurité — Régularisation N+1
            </div>
            <div className="text-sm mb-4" style={{ color: C.muted }}>
              Si votre CA augmente cette année, l'URSSAF et les impôts vous demanderont un rattrapage l'année prochaine. Voici combien mettre de côté dès maintenant.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="rounded-xl p-4 text-center" style={{ backgroundColor: C.light }}>
                <div className="text-xs font-semibold uppercase mb-1" style={{ color: C.muted }}>Rattrapage cotisations</div>
                <div className="text-lg font-bold" style={{ color: C.blue }}>{fmt(calc.regularisationN1)}</div>
              </div>
              <div className="rounded-xl p-4 text-center" style={{ backgroundColor: C.light }}>
                <div className="text-xs font-semibold uppercase mb-1" style={{ color: C.muted }}>Rattrapage IR</div>
                <div className="text-lg font-bold" style={{ color: C.warning }}>{fmt(calc.regularisationIR)}</div>
              </div>
              <div className="rounded-xl p-4 text-center" style={{ backgroundColor: C.light }}>
                <div className="text-xs font-semibold uppercase mb-1" style={{ color: C.muted }}>Marge sécurité (+2%)</div>
                <div className="text-lg font-bold" style={{ color: C.muted }}>{fmt(calc.margeSecurite)}</div>
              </div>
            </div>

            <div className="rounded-xl p-4" style={{ backgroundColor: C.lightTeal, border: `1px solid ${C.teal}` }}>
              <div className="text-sm font-semibold" style={{ color: C.teal }}>
                💰 Pour dormir tranquille, mettez de côté <strong>{fmt(calc.tresorerieMensuelle)}/mois</strong> dès maintenant pour couvrir votre régularisation de l'année prochaine.
              </div>
              <div className="text-xs mt-2" style={{ color: C.muted }}>
                Total à provisionner : {fmt(calc.tresorerieSecurite)} sur 12 mois (dont {fmt(calc.margeSecurite)} de marge de sécurité)
              </div>
            </div>

            {!isMicroBNC && calc.chargesFixesMensuelles > 0 && (
              <div className="mt-3 text-xs" style={{ color: C.muted }}>
                📋 Rappel : vos charges fixes de {fmt(calc.chargesFixesMensuelles)}/mois restent dues même les mois sans activité.
              </div>
            )}
          </Card>
        )}

        {/* ═══ OBJECTIF INVERSÉ ═══ */}
        <div className="rounded-xl p-5 shadow-sm" style={{ backgroundColor: C.navy, borderRadius: 12 }}>
          <div className="font-bold text-sm mb-4 text-white">
            🎯 Combien de patients pour atteindre mon objectif ?
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
            <span className="text-sm text-white/70">Je veux gagner</span>
            <div className="relative">
              <input
                type="number" min="0" step="100" value={objectifNet}
                onChange={e => setObjectifNet(Math.max(0, Number(e.target.value) || 0))}
                className="rounded-lg border-0 px-4 py-2.5 text-sm font-bold w-36 outline-none"
                style={{ backgroundColor: "rgba(255,255,255,0.15)", color: C.white }}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-white/50">€/mois</span>
            </div>
            <span className="text-sm text-white/70">nets par mois</span>
          </div>
          <div className="rounded-xl p-4" style={{ backgroundColor: "rgba(255,255,255,0.1)" }}>
            <div className="text-white text-sm">
              Pour <strong style={{ color: "#7EC8C0" }}>{fmt(objectifNet)} nets/mois</strong> il vous faut{" "}
              <strong style={{ color: "#7EC8C0" }}>{objectifCalc.patients} patients/semaine</strong> à{" "}
              <strong style={{ color: "#7EC8C0" }}>{tarif}€</strong> la séance
            </div>
            <div className="text-xs text-white/50 mt-2">
              CA nécessaire : {fmt(objectifCalc.ca)} · URSSAF : {fmt(objectifCalc.urssaf)} · IR : {fmt(objectifCalc.ir)}
              {!isMicroBNC && ` · Charges : ${fmt(objectifCalc.charges)}`}
            </div>
          </div>
          <button
            onClick={() => {
              setPatients(objectifCalc.patients);
              setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
            }}
            className="mt-3 px-4 py-2 rounded-lg text-xs font-semibold transition-colors"
            style={{ backgroundColor: C.teal, color: C.white }}
          >
            Utiliser ce scénario ↑
          </button>
        </div>

        {/* ═══ RÉSUMÉ CLAIR ═══ */}
        <Card>
          <div className="font-bold text-sm mb-3" style={{ color: C.navy }}>
            Ce que je gagne · Ce que je verse · Ce qu'il me reste vraiment
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl p-3" style={{ backgroundColor: C.lightTeal }}>
              <div className="text-xs font-semibold uppercase mb-1" style={{ color: C.teal }}>J'encaisse</div>
              <div className="text-lg font-bold" style={{ color: C.teal }}>{fmt(calc.ca)}</div>
              <div className="text-[10px]" style={{ color: C.muted }}>CA annuel brut</div>
            </div>
            <div className="rounded-xl p-3" style={{ backgroundColor: "#FFF5EC" }}>
              <div className="text-xs font-semibold uppercase mb-1" style={{ color: C.warning }}>Je verse</div>
              <div className="text-lg font-bold" style={{ color: C.warning }}>{fmt(calc.urssaf + calc.ir + calc.charges)}</div>
              <div className="text-[10px]" style={{ color: C.muted }}>Cotisations + IR + Charges</div>
            </div>
            <div className="rounded-xl p-3" style={{ backgroundColor: C.indigoBg }}>
              <div className="text-xs font-semibold uppercase mb-1" style={{ color: C.indigo }}>Vrai Net</div>
              <div className="text-lg font-bold" style={{ color: C.indigo }}>{fmt(Math.max(0, calc.net))}</div>
              <div className="text-[10px]" style={{ color: C.muted }}>{fmt(Math.max(0, calc.netMois))}/mois</div>
            </div>
          </div>
        </Card>

        {/* ═══ DISCLAIMER + TRANSPARENCE ═══ */}
        <div className="rounded-xl p-4 border-l-4" style={{ borderLeftColor: C.teal, backgroundColor: C.lightTeal }}>
          <div className="text-xs leading-relaxed" style={{ color: C.navy }}>
            🤝 Ces chiffres sont des simulations basées sur les taux 2025/2026 (ARAPL, CIPAV, Service-Public.fr). Parce que nous respectons votre travail, nous avons ajouté une <strong>marge de sécurité de 2%</strong> dans le calcul de la trésorerie pour éviter toute mauvaise surprise. Ce ne sont pas des documents comptables officiels, mais votre meilleure protection contre l'imprévu.
          </div>
        </div>
        <div className="rounded-xl p-4 border-l-4" style={{ borderLeftColor: C.navy, backgroundColor: C.light }}>
          <div className="text-xs" style={{ color: C.navy }}>
            📌 {isMicroBNC ? "Cotisations CIPAV : 23,2% du CA + 0,2% CFP = 23,4% — Abattement Micro-BNC : 34% — Seuil 2026-2028 : 83 600€" : "Cotisations BNC classique estimées à ~40% du bénéfice net (taux moyen constaté 35-45%). Vos cotisations réelles sont calculées par l'URSSAF sur votre bénéfice net déclaré."}. IR : barème progressif 2026. Exonération TVA sur les soins psychologiques (Art. 261, 4-1° du CGI). Ces estimations sont indicatives et ne remplacent pas les conseils d'un expert-comptable.
          </div>
        </div>

        <div className="flex justify-center pb-6">
          <button onClick={onRetour} className="px-6 py-3 rounded-xl text-sm font-bold text-white" style={{ backgroundColor: C.teal }}>
            ← Retour au tableau de bord
          </button>
        </div>
      </div>
    </div>
  );
}
